import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, CardBody, CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
  Button, TextField, MuiThemeProvider, createMuiTheme,
  FormControl, InputLabel, FormHelperText, Checkbox, FormControlLabel,
  MenuItem
} from '@material-ui/core';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { useHistory, useParams } from 'react-router-dom';
import {
  deleteArticle,
  getArticleById,
  postArticle,
  updateArticle,
} from '../../../../api/article';
import { useSkeleton } from '../../../hooks/useSkeleton';
import PreviewDialog from '../../../components/dialogs/PreviewDialog';
import { SERVER_URL } from '../../../../utils';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';

// Botón rojo "Delete"
const theme = createMuiTheme({
  palette: {
    secondary: { main: '#F64E60' },
  },
});

function getEmptyArticle() {
  return {
    // antiguos
    fullName: '',           // lo mantenemos por compatibilidad si lo usas en otro lado
    image: '',
    // nuevos (MVP)
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    status: 'draft',        // draft | published | scheduled | archived
    publishedAt: '',        // ISO string (yyyy-mm-ddThh:mm)
    featured: false,
    active: true,
    author: '',             // por simplicidad, string (puedes cambiar a ref)
    categories: [],         // ids o strings
    tags: [],               // strings
    cover: { url: '', alt: '', caption: '' },

    // útiles
    allowComments: true,
    readingTime: '',
    source: '',
    canonicalUrl: '',
    seo: { metaTitle: '', metaDescription: '', ogImage: '' },
    related: [],            // ids (otros artículos)
    locale: 'es',
    translations: [],       // [{ locale, article }]
    expiresAt: '',
    pinUntil: '',
  };
}

export default function EditArticlesPage() {
  const [article, setArticle] = useState(getEmptyArticle());
  const [selectedImage, setSelectedImage] = useState(null);     // image (legacy)
  const [selectedCover, setSelectedCover] = useState(null);     // cover file
  const [openPreviewImage, setOpenPreviewImage] = useState(false);
  const [openPreviewCover, setOpenPreviewCover] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const articleId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!articleId) { disableLoadingData(); return; }
    getArticleById(articleId)
      .then((res) => {
        if (res.status === 200) {
          // Asegura estructura por si backend aún no devuelve todos los campos
          setArticle({ ...getEmptyArticle(), ...res.data });
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'Could not get article.' });
        history.push('/articles');
      });
  }, [articleId, disableLoadingData, history]);

  const handleChange = (key) => (event) => {
    setArticle({ ...article, [key]: event.target.value });
  };

  const handleNestedChange = (path) => (event) => {
    const value = event.target.value;
    setArticle(prev => {
      const next = { ...prev };
      const segs = path.split('.');
      let ref = next;
      for (let i = 0; i < segs.length - 1; i++) {
        const s = segs[i];
        ref[s] = ref[s] || {};
        ref = ref[s];
      }
      ref[segs[segs.length - 1]] = value;
      return next;
    });
  };

  const handleBoolean = (key) => (event) => {
    setArticle({ ...article, [key]: event.target.checked });
  };

  const handleDate = (key) => (event) => {
    setArticle({ ...article, [key]: event.target.value });
  };

  // react-select helpers
  const categoriesOptions = useMemo(() => {
    // Si no tienes catálogo, puedes mapear strings a {value,label}
    return (article.categories || []).map(c => (
      typeof c === 'string' ? { value: c, label: c } : { value: c?._id || c.value, label: c?.name || c.label || String(c) }
    ));
  }, [article.categories]);

  const tagsOptions = useMemo(() => {
    return (article.tags || []).map(t => ({ value: t, label: t }));
  }, [article.tags]);

  function saveArticle() {
	const files = {
		image: selectedImage || null, // legacy image
		cover: selectedCover || null, // new cover
	};

	const req = !articleId
		? postArticle(article, files)
		: updateArticle(articleId, article, files);

	req
		.then((res) => {
		if (res.status === 201 || res.status === 200) {
			alertSuccess({
			title: 'Saved!',
			customMessage: articleId ? 'Changes successfully saved.' : 'Article successfully created.',
			});
			history.push('/articles');
		}
		})
		.catch((error) => {
		alertError({ error, customMessage: 'Could not save article.' });
		});
  }


  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title={articleId ? 'Edit article' : 'Create article'} />
        <CardBody>

          {/* Title + Slug */}
          <TextField
            id="title"
            label="Title"
            value={article.title || ''}
            onChange={handleChange('title')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            required
            fullWidth
          />
          <TextField
            id="slug"
            label="Slug"
            value={article.slug || ''}
            onChange={handleChange('slug')}
            helperText="Deja vacío para autogenerar en backend"
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            fullWidth
          />

          {/* Excerpt */}
          <TextField
            id="excerpt"
            label="Excerpt"
            value={article.excerpt || ''}
            onChange={handleChange('excerpt')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            multiline
            rows={3}
            fullWidth
          />

          {/* Content (simple textarea por ahora) */}
          <TextField
            id="content"
            label="Content (HTML/Markdown/JSON)"
            value={article.content || ''}
            onChange={handleChange('content')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            multiline
            rows={8}
            fullWidth
          />

          {/* Status + PublishedAt */}
          <FormControl variant="outlined" margin="normal">
            <TextField
              select
              id="status"
              label="Status"
              value={article.status || 'draft'}
              onChange={handleChange('status')}
              variant="outlined"
            >
              {['draft','published','scheduled','archived'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <FormHelperText>Status de publicación</FormHelperText>
          </FormControl>

          <TextField
            id="publishedAt"
            type="datetime-local"
            label="Published at"
            value={article.publishedAt ? article.publishedAt.substring(0,16) : ''}
            onChange={handleDate('publishedAt')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
          />

          {/* Flags */}
          <div style={{ marginTop: 12 }}>
            <FormControlLabel
              control={<Checkbox color="primary" checked={!!article.featured} onChange={handleBoolean('featured')} />}
              label="Featured"
            />
            <FormControlLabel
              control={<Checkbox color="primary" checked={!!article.active} onChange={handleBoolean('active')} />}
              label="Active"
            />
            <FormControlLabel
              control={<Checkbox color="primary" checked={!!article.allowComments} onChange={handleBoolean('allowComments')} />}
              label="Allow comments"
            />
          </div>

          {/* Author */}
          <TextField
            id="author"
            label="Author"
            value={article.author || ''}
            onChange={handleChange('author')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
          />

          {/* Categories (multi) */}
          <div style={{ marginTop: 16 }}>
            <InputLabel shrink>Categories</InputLabel>
            <Select
              isMulti
              value={categoriesOptions}
              onChange={(vals) => setArticle({
                ...article,
                categories: (vals || []).map(v => v.value || v),
              })}
              placeholder="Select categories"
            />
            <FormHelperText>Puedes mapear a IDs o nombres</FormHelperText>
          </div>

          {/* Tags (multi + creatable) */}
          <div style={{ marginTop: 16 }}>
            <InputLabel shrink>Tags</InputLabel>
            <CreatableSelect
              isMulti
              value={tagsOptions}
              onChange={(vals) => setArticle({
                ...article,
                tags: (vals || []).map(v => v.value),
              })}
              placeholder="Add or select tags"
            />
          </div>

          {/* Reading time, Source, Canonical */}
          <TextField
            id="readingTime"
            label="Reading time (min)"
            type="number"
            value={article.readingTime || ''}
            onChange={handleChange('readingTime')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
          />
          <TextField
            id="source"
            label="Source"
            value={article.source || ''}
            onChange={handleChange('source')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            fullWidth
          />
          <TextField
            id="canonicalUrl"
            label="Canonical URL"
            value={article.canonicalUrl || ''}
            onChange={handleChange('canonicalUrl')}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            fullWidth
          />

          {/* SEO */}
          <Card style={{ marginTop: 24 }}>
            <CardHeader title="SEO" />
            <CardBody>
              <TextField
                id="seo.metaTitle"
                label="Meta title"
                value={article.seo?.metaTitle || ''}
                onChange={handleNestedChange('seo.metaTitle')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
              />
              <TextField
                id="seo.metaDescription"
                label="Meta description"
                value={article.seo?.metaDescription || ''}
                onChange={handleNestedChange('seo.metaDescription')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                id="seo.ogImage"
                label="OpenGraph image URL"
                value={article.seo?.ogImage || ''}
                onChange={handleNestedChange('seo.ogImage')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
              />
            </CardBody>
          </Card>

          {/* Cover upload */}
          <Card style={{ marginTop: 24 }}>
            <CardHeader title="Cover" />
            <CardBody>
              <label className="mt-2" htmlFor="upload-cover">
                <input
                  style={{ display: 'none' }}
                  id="upload-cover"
                  name="upload-cover"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedCover(e.target.files[0])}
                />
                <Button style={{ marginRight: 15 }} color="secondary" component="span" variant="outlined">
                  {selectedCover || article?.cover?.url ? 'Change cover' : 'Upload cover'}
                </Button>
              </label>
              {(selectedCover || article?.cover?.url) && (
                <>
                  <Button onClick={() => setOpenPreviewCover(true)} style={{ marginRight: 15 }} variant="outlined">
                    Preview cover
                  </Button>
                  <PreviewDialog
                    title="Preview cover"
                    open={openPreviewCover}
                    setOpen={setOpenPreviewCover}
                    src={selectedCover ? URL.createObjectURL(selectedCover) : `${SERVER_URL}/${article.cover.url}`}
                  />
                </>
              )}
              <TextField
                id="cover.alt"
                label="Cover alt"
                value={article.cover?.alt || ''}
                onChange={handleNestedChange('cover.alt')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
              />
              <TextField
                id="cover.caption"
                label="Cover caption"
                value={article.cover?.caption || ''}
                onChange={handleNestedChange('cover.caption')}
                InputLabelProps={{ shrink: true }}
                margin="normal"
                variant="outlined"
                fullWidth
              />
            </CardBody>
          </Card>

          {/* Locale / fechas especiales */}
          <div style={{ marginTop: 24 }}>
            <TextField
              id="locale"
              label="Locale"
              value={article.locale || 'es'}
              onChange={handleChange('locale')}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              style={{ marginRight: 16 }}
            />
            <TextField
              id="pinUntil"
              type="datetime-local"
              label="Pin until"
              value={article.pinUntil ? article.pinUntil.substring(0,16) : ''}
              onChange={handleDate('pinUntil')}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              style={{ marginRight: 16 }}
            />
            <TextField
              id="expiresAt"
              type="datetime-local"
              label="Expires at"
              value={article.expiresAt ? article.expiresAt.substring(0,16) : ''}
              onChange={handleDate('expiresAt')}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
            />
          </div>

          {/* Related (IDs separados por coma para simplificar) */}
          <TextField
            id="related"
            label="Related article IDs (comma separated)"
            value={(article.related || []).join(',')}
            onChange={(e) => setArticle({ ...article, related: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
            InputLabelProps={{ shrink: true }}
            margin="normal"
            variant="outlined"
            fullWidth
          />
        </CardBody>
      </Card>

      <Button onClick={() => history.push('/articles')} variant='outlined' style={{ marginRight: 20 }}>
        Back
      </Button>
      <Button onClick={saveArticle} variant='outlined' color='primary' style={{ marginRight: 20 }}>
        Save article
      </Button>

      {articleId && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button onClick={() => setOpenConfirmDialog(true)} variant='outlined' color='secondary'>
              Delete article
            </Button>
          </MuiThemeProvider>

          <ConfirmDialog
            title={'Are you sure you want to delete this article?'}
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteArticle(articleId)
                .then((res) => {
                  if (res.status === 204) {
                    alertSuccess({ title: 'Deleted!', customMessage: 'Article deleted successfully' });
                    history.push('/articles');
                  }
                })
                .catch((error) => {
                  alertError({ error, customMessage: 'Could not delete article.' });
                });
            }}
          />
        </>
      )}
    </>
  );
}
